import classNames from 'classnames';
import React, { Component, type CSSProperties, type ReactNode } from 'react';
import './Tabs.scss';

type TabsProps = {
  children: any;
  className?: string;
  footer?: ReactNode;
  activeTabIndex?: number;
  onSelect?: (param: number) => void;
  css?: CSSProperties | string;
};

type TabsState = {
  activeTab: number;
  underlineOffsetLeft: number;
  underlineWidth: number;
};

export class Tabs extends Component<TabsProps, TabsState> {
  activeElement: React.RefObject<HTMLButtonElement | null>;

  constructor(props) {
    super(props);

    this.activeElement = React.createRef<HTMLButtonElement | null>();

    this.state = {
      activeTab: this.props.activeTabIndex || 0,
      underlineOffsetLeft: 0,
      underlineWidth: 0
    };
  }

  onSelectTab(element, index: number) {
    if (element !== null) {
      this.setState({
        activeTab: index,
        underlineOffsetLeft: (element.offsetLeft + (element.children[0]?.offsetLeft ?? 0)),
        underlineWidth: element.children[0]?.offsetWidth ?? 0
      });
    }

    if (this.props.onSelect) {
      this.props.onSelect(index);
    }
  }

  componentDidMount() {
    const tabTitles = this.props.children.map(
      element => element.props.label || element.props['data-tab-label']
    );
    const tabShown = this.props.children.map(element => element.props.hidden);
    let activeTab = this.state.activeTab;

    for (let i = 0; i < tabTitles.length; i++) {
      if (this.state.activeTab === i && tabShown[i]) {
        activeTab = this.state.activeTab + 1;
      }
    }

    this.setState({ activeTab: Math.min(activeTab, tabTitles.length - 1) });

    setTimeout(
      () => this.onSelectTab(this.activeElement.current, activeTab),
      300
    );
  }

  componentDidUpdate(prevProps) {
    /**
     * If last tab is selected, and children updates so that last tab will be removed.
     * We auto select the last element of the left over tab pages.
     * This can happen for example with Search filter, when such a filter combination is selected
     * that no productionYears or fileExtensions are returned and the corresponding tab page has to be removed.
     */
    if (
      this.props.children.length < prevProps.children.length &&
      this.state.activeTab === prevProps.children.length - 1
    ) {
      const nextTabs = document.querySelectorAll('.b-tabs-button');
      const nextLastTab = nextTabs[nextTabs.length - 1];
      this.onSelectTab(nextLastTab, this.props.children.length - 1);
    }
  }

  render() {
    const tabTitles = this.props.children.map(
      element => element.props.label || element.props['data-tab-label']
    );
    const tabShown = this.props.children.map(element => element.props.hidden);

    return (
      <div
        className="b-tabs"
        style={typeof this.props.css === 'object' ? this.props.css : undefined}
      >
        <div className="b-tabs-button-container" role="tablist">
          {tabTitles.map((title, index) => {
            const classes = classNames({
              'b-tabs-button': true,
              [`tab-button-${index}`]: true,
              active: this.state.activeTab === index,
              'is-hidden': tabShown[index]
            });

            return (
              <button
                type="button"
                role="tab"
                //@ts-ignore
                ref={
                  this.state.activeTab === index && !tabShown[index]
                    ? this.activeElement
                    : null
                }
                key={title}
                className={classes}
                onClick={e => {
                  this.onSelectTab(e.currentTarget, index);
                }}
                data-testid={title}
              >
                <span className="b-tabs__label">{title}</span>
              </button>
            );
          })}
          <div
            className="tab-underline"
            style={{
              width: `${this.state.underlineWidth}px`,
              left: `${this.state.underlineOffsetLeft}px`
            }}
          />
        </div>
        <div className="b-tabs-wrapper">
          <div className="b-tabs-content">
            {this.props.children[this.state.activeTab]}
          </div>
          {this.props.footer && this.props.footer}
        </div>
      </div>
    );
  }
}
